import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag, ShieldCheck, ArrowRight, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { createCheckout } from "@/lib/api";

const Cart = () => {
  const { items, updateQty, removeItem, total, keyOf, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          variant_id: i.variant_id,
          psu: i.psu,
          fans: i.fans,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
      };
      const { url } = await createCheckout(payload);
      window.location.href = url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="mx-auto flex max-w-7xl flex-col items-center px-5 py-32 text-center">
        <ShoppingBag size={48} className="text-[#27272A]" />
        <h1 className="mt-6 font-heading text-3xl font-semibold text-[#FAFAFA]">Your cart is empty</h1>
        <p className="mt-3 text-[#A1A1AA]">Add a shroud and start mining quieter.</p>
        <Link to="/shop" data-testid="cart-shop-link" className="mt-8 inline-flex items-center gap-2 rounded-sm bg-primary px-7 py-3.5 text-sm font-semibold text-[#09090B] transition-all hover:bg-[#EA580C]">
          Browse products <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const shipping = total >= 100 ? 0 : 9.99;
  const grand = total + shipping;

  return (
    <div data-testid="cart-page" className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
      <h1 className="font-heading text-4xl font-semibold tracking-tighter text-[#FAFAFA]">Your cart</h1>

      <div className="mt-10 grid gap-10 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {items.map((item) => {
            const k = keyOf(item);
            return (
              <motion.div
                key={k}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid={`cart-item-${item.product_id}`}
                className="flex gap-4 rounded-sm border border-[#27272A] bg-[#0E0E11] p-4"
              >
                <img src={item.image} alt={item.name} className="h-24 w-24 rounded-sm object-cover" />
                <div className="flex flex-1 flex-col">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-base font-medium text-[#FAFAFA]">{item.name}</h3>
                      <p className="text-xs text-[#A1A1AA]">{item.variant_label}</p>
                    </div>
                    <button data-testid={`remove-${item.product_id}`} onClick={() => removeItem(k)} className="text-[#A1A1AA] hover:text-destructive">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex items-center rounded-sm border border-[#27272A]">
                      <button onClick={() => updateQty(k, item.quantity - 1)} className="flex h-9 w-9 items-center justify-center text-[#FAFAFA] hover:text-primary"><Minus size={14} /></button>
                      <span className="w-8 text-center font-mono text-sm text-[#FAFAFA]">{item.quantity}</span>
                      <button onClick={() => updateQty(k, item.quantity + 1)} className="flex h-9 w-9 items-center justify-center text-[#FAFAFA] hover:text-primary"><Plus size={14} /></button>
                    </div>
                    <span className="font-mono text-base font-semibold text-[#FAFAFA]">€{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
          <button onClick={clearCart} data-testid="clear-cart" className="text-xs text-[#A1A1AA] hover:text-destructive">Clear cart</button>
        </div>

        {/* Summary */}
        <div className="h-fit rounded-sm border border-[#27272A] bg-[#0E0E11] p-6">
          <h2 className="font-heading text-lg font-medium text-[#FAFAFA]">Order summary</h2>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between text-[#A1A1AA]"><span>Subtotal</span><span className="font-mono text-[#FAFAFA]" data-testid="cart-subtotal">€{total.toFixed(2)}</span></div>
            <div className="flex justify-between text-[#A1A1AA]"><span>Shipping</span><span className="font-mono text-[#FAFAFA]">{shipping === 0 ? "Free" : `€${shipping.toFixed(2)}`}</span></div>
            {total < 100 && <p className="text-xs text-primary">Add €{(100 - total).toFixed(2)} more for free shipping.</p>}
            <div className="border-t border-[#27272A] pt-3" />
            <div className="flex justify-between text-base"><span className="text-[#FAFAFA]">Total</span><span className="font-mono font-semibold text-[#FAFAFA]" data-testid="cart-total">€{grand.toFixed(2)}</span></div>
          </div>
          <button
            onClick={checkout}
            disabled={loading}
            data-testid="checkout-button"
            className="group mt-6 flex w-full items-center justify-center gap-2 rounded-sm bg-primary px-7 py-3.5 text-sm font-semibold text-[#09090B] transition-all hover:bg-[#EA580C] hover:shadow-[0_0_20px_rgba(249,115,22,0.4)] disabled:opacity-60"
          >
            {loading ? "Redirecting…" : <><Lock size={15} /> Secure checkout</>}
          </button>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#A1A1AA]">
            <ShieldCheck size={14} className="text-primary" /> Encrypted payment via Stripe
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
